import type DataGrid from 'devextreme/ui/data_grid';
import type { CellPreparedEvent, Column } from 'devextreme/ui/data_grid';
import ArrayStore from 'devextreme/data/array_store';
import { formatDate, formatNumber } from 'devextreme/localization';

interface SearchColumn extends Column {
  parseValue?: (value: unknown) => unknown;
  userDataType?: string;
}

interface ParsedSearchValue {
  parsedValue: unknown;
  searchText: string;
}

type SearchDataGrid = DataGrid & {
  getController(name: 'data'): {
    _dataSource?: { remoteOperations(): { filtering?: boolean } };
  };
};

const configuredDataGrids = new WeakSet<DataGrid>();
const customizedParseValues = new WeakSet<(value: unknown) => unknown>();
const dateFormats = ['dd.MM.yyyy', 'dd.MM.yyyy HH:mm', 'dd.MM.yyyy hh:mm'];
const highlightSelector = 'span[data-date-search]';

// Aktiviert Teilstringsuche und Hervorhebung für geeignete Datums- und Zahlenspalten.
export function configureDataGridDateSearch(dataGrid: DataGrid): void {
  if (!dataGrid || configuredDataGrids.has(dataGrid)) return;
  configuredDataGrids.add(dataGrid);

  let hasSearchColumns = false;
  let initialSearchApplied = false;
  const customizeColumns = dataGrid.option('customizeColumns');
  // Ergänzt geeignete Spalten um die Suche nach ihrem formatierten Wert.
  dataGrid.option('customizeColumns', function (columns: Column[]) {
    customizeColumns?.call(this, columns);
    for (const column of columns as SearchColumn[]) {
      if (!isSearchColumn(column) || (column.parseValue && customizedParseValues.has(column.parseValue))) continue;

      const parseValue = column.parseValue;
      const calculateFilterExpression = column.calculateFilterExpression;
      // Behält den eingegebenen Suchtext neben dem geparsten Wert.
      const customizedParseValue = function (this: SearchColumn, value: unknown): unknown {
        const parsedValue = parseValue?.call(this, value);
        if (typeof value !== 'string' || !isSearchColumn(this)) return parsedValue;
        return { parsedValue, searchText: value };
      };
      customizedParseValues.add(customizedParseValue);
      column.parseValue = customizedParseValue;
      hasSearchColumns = true;

      // Erstellt lokale oder serverseitige Teilstringfilter für die Suche.
      column.calculateFilterExpression = function (filterValue: unknown, selectedFilterOperation: string | null, target: string) {
        const parsed = isParsedSearchValue(filterValue);
        if (target === 'search' && parsed && isSearchColumn(this)) {
          const searchText = filterValue.searchText;
          if ((dataGrid as SearchDataGrid).getController('data')._dataSource?.remoteOperations().filtering &&
              (this.dataType !== 'number' || !(dataGrid.getDataSource()?.store() instanceof ArrayStore))) {
            const operation = this.format === 'dd.MM.yyyy HH:mm' ? 'containsDateTime' :
              this.format === 'dd.MM.yyyy hh:mm' ? 'containsDateTime12' : 'contains';
            const filter = [this.dataField, 'contains',
              this.dataType === 'number' && hasTwoDecimalPlaces(this.format) ? `\uE0002:${searchText}` : searchText];
            if (operation === 'contains') return filter;
            return Object.assign(filter, {
              // Überträgt das Uhrzeitformat als eigenen Filteroperator an den Server.
              toJSON() { return [this[0], operation, this[2]]; },
            });
          }

          // Liest für lokale Filter den formatierten Wert jeder Zeile.
          const rawFilter = [(rowData: unknown) => formatSearchValue(this.calculateCellValue(rowData), this), 'contains', searchText];
          if (this.dataType !== 'number' || !hasTwoDecimalPlaces(this.format)) return rawFilter;
          return [rawFilter, 'or', [(rowData: unknown) => formatNumberSearchValue(this.calculateCellValue(rowData), this), 'contains', searchText]];
        }

        return calculateFilterExpression.call(this, parsed ? filterValue.parsedValue : filterValue, selectedFilterOperation, target);
      };
    }
  });
  // Wendet einen vorhandenen Suchtext nach dem ersten Laden erneut an.
  dataGrid.on('contentReady', () => {
    if (initialSearchApplied || !hasSearchColumns) return;
    initialSearchApplied = true;
    if (dataGrid.option('searchPanel.text')) dataGrid.refresh();
  });
  dataGrid.on('cellPrepared', highlightSearchText);
  // Zeichnet die Markierungen nach einer Änderung der Suchoption neu.
  dataGrid.on('optionChanged', (event) => {
    if (event.fullName === 'searchPanel.highlightSearchText') dataGrid.repaint();
  });
}

// Erkennt Suchwerte mit Originaltext und geparstem Wert.
function isParsedSearchValue(value: unknown): value is ParsedSearchValue {
  return typeof value === 'object' && value !== null && 'parsedValue' in value && 'searchText' in value;
}

// Wählt einfache Zahlen- und Datumsspalten mit unterstütztem Format aus.
function isSearchColumn(column: SearchColumn): boolean {
  return Boolean(
    column.dataField &&
      column.userDataType === column.dataType &&
      (column.dataType === 'number' ||
        ((column.dataType === 'date' || column.dataType === 'datetime') &&
          typeof column.format === 'string' && dateFormats.includes(column.format))) &&
      !column.lookup && !column.customizeText && !column.calculateDisplayValue &&
      column.calculateCellValue === column.defaultCalculateCellValue &&
      (column.allowSearch ?? column.allowFiltering)
  );
}

// Formatiert einen Zellwert für den Vergleich mit dem Suchtext.
function formatSearchValue(value: unknown, column: Column): string {
  if (value == null) return '';
  if (column.dataType === 'number') return String(value).replace('.', ',');
  const format = column.format as string;
  if (typeof value === 'string' && format === 'dd.MM.yyyy') {
    const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (day) return `${day[3]}.${day[2]}.${day[1]}`;
  }

  const date = value instanceof Date ? value : typeof value === 'string' || typeof value === 'number' ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return formatDate(date, format);
}

function hasTwoDecimalPlaces(format: Column['format']): format is string {
  return typeof format === 'string' && /\.00(?:\D|$)/.test(format);
}

function formatNumberSearchValue(value: unknown, column: Column): string {
  return typeof value === 'number' && hasTwoDecimalPlaces(column.format) ?
    formatNumber(value, column.format).replace(/\./g, '').replace(/[^\d,-]/g, '') : '';
}

// Ordnet Zeichen des Zahlenwerts ihren Positionen im angezeigten Text zu.
function numericDisplayPositions(searchValue: string, displayText: string): (number | undefined)[] {
  const positions: (number | undefined)[] = [];
  let displayIndex = 0;
  for (const character of searchValue) {
    if (/\d/.test(character)) {
      while (displayIndex < displayText.length && !/\d/.test(displayText[displayIndex])) displayIndex++;
      if (displayText[displayIndex] !== character) break;
      positions.push(displayIndex++);
    } else if (character === ',') {
      const nextDigit = displayText.slice(displayIndex).search(/\d/);
      if (nextDigit < 0) break;
      const separator = displayText.slice(displayIndex, displayIndex + nextDigit).search(/[,.]/);
      if (separator < 0) break;
      positions.push(displayIndex + separator);
      displayIndex += separator + 1;
    } else if (character === '-') {
      const sign = displayText.slice(displayIndex).indexOf('-');
      positions.push(sign < 0 ? undefined : displayIndex + sign);
      if (sign >= 0) displayIndex += sign + 1;
    } else {
      break;
    }
  }
  return positions;
}

// Entfernt alte Markierungen und hebt Treffer im angezeigten Zelltext hervor.
function highlightSearchText(event: CellPreparedEvent): void {
  const cell = event.cellElement as HTMLElement;
  if (!cell?.ownerDocument) return;

  const highlights = cell.querySelectorAll(highlightSelector);
  for (const highlight of highlights) highlight.replaceWith(...highlight.childNodes);
  if (highlights.length) cell.normalize();

  if (event.rowType !== 'data' || event.row?.isEditing || !event.column || !isSearchColumn(event.column) ||
      !event.component.option('searchPanel.highlightSearchText')) return;

  const searchText = String(event.component.option('searchPanel.text') ?? '');
  const isNumber = event.column.dataType === 'number';
  const displayText = isNumber ? event.text ?? '' : formatSearchValue(event.value, event.column);
  const rawSearchValue = isNumber ? formatSearchValue(event.value, event.column) : displayText;
  const formattedSearchValue = isNumber ? formatNumberSearchValue(event.value, event.column) : '';
  const searchValue = isNumber && displayText.includes(searchText) ? displayText :
    formattedSearchValue.includes(searchText) ? formattedSearchValue : rawSearchValue;
  if (!searchText || !searchValue.includes(searchText)) return;

  const positions = isNumber && searchValue !== displayText ? numericDisplayPositions(searchValue, displayText) : null;
  const matches: { start: number; end: number }[] = [];
  let matchIndex = searchValue.indexOf(searchText);
  while (matchIndex >= 0) {
    const start = positions ? positions[matchIndex] : matchIndex;
    const end = positions ? positions[matchIndex + searchText.length - 1] : matchIndex + searchText.length - 1;
    if (start !== undefined && end !== undefined) matches.push({ start, end: end + 1 });
    matchIndex = searchValue.indexOf(searchText, matchIndex + searchText.length);
  }
  if (!matches.length) return;

  const textNodes: Text[] = [];
  const walker = cell.ownerDocument.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (!node.parentElement?.closest('button, input, textarea, select, [contenteditable], [hidden], [aria-hidden="true"], script, style')) {
      textNodes.push(node);
    }
  }

  for (const node of textNodes) {
    const displayIndex = node.data.indexOf(displayText);
    if (displayIndex < 0) continue;
    const fragment = cell.ownerDocument.createDocumentFragment();
    fragment.append(node.data.slice(0, displayIndex));
    let textIndex = 0;
    for (const match of matches) {
      fragment.append(displayText.slice(textIndex, match.start));
      const highlight = cell.ownerDocument.createElement('span');
      highlight.classList.add('dx-datagrid-search-text');
      highlight.setAttribute('data-date-search', '');
      highlight.textContent = displayText.slice(match.start, match.end);
      fragment.append(highlight);
      textIndex = match.end;
    }
    fragment.append(displayText.slice(textIndex), node.data.slice(displayIndex + displayText.length));
    node.replaceWith(fragment);
  }
}
